/**
 * Session Cost Preview Component
 *
 * Displays estimated credit cost before session generation.
 * Shows warnings for insufficient credits or tier limits.
 */

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { AlertCircle, AlertTriangle, CheckCircle2, Coins } from 'lucide-react';
import {
  useSessionCostEstimate,
  formatCreditCost,
  getCostWarningMessage,
} from '@/hooks/useSessionCostEstimate';

interface SessionCostPreviewProps {
  /** Session duration in minutes */
  durationMinutes: number;
  /** Whether to show the component */
  show?: boolean;
  /** Compact mode for inline display */
  compact?: boolean;
  /** Callback when cost is calculated */
  onCostCalculated?: (canAfford: boolean, creditsRequired: number) => void;
}

/**
 * Component to preview session cost before generation
 *
 * @example
 * ```tsx
 * <SessionCostPreview
 *   durationMinutes={10}
 *   onCostCalculated={(canAfford) => setCanSubmit(canAfford)}
 * />
 * ```
 */
export function SessionCostPreview({
  durationMinutes,
  show = true,
  compact = false,
  onCostCalculated,
}: SessionCostPreviewProps) {
  const { data: estimate, isLoading, error } = useSessionCostEstimate({
    duration_minutes: durationMinutes,
    enabled: show && durationMinutes > 0,
  });

  // Notify parent of cost calculation result
  if (estimate && onCostCalculated) {
    onCostCalculated(estimate.can_afford && !estimate.duration_exceeds_limit, estimate.credits_required);
  }

  if (!show || durationMinutes <= 0) {
    return null;
  }

  if (isLoading) {
    return compact ? (
      <Skeleton className="h-5 w-32" />
    ) : (
      <div className="p-4 border rounded-lg">
        <Skeleton className="h-4 w-48 mb-2" />
        <Skeleton className="h-4 w-32" />
      </div>
    );
  }

  if (error) {
    return compact ? null : (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Error</AlertTitle>
        <AlertDescription>Could not estimate session cost</AlertDescription>
      </Alert>
    );
  }

  if (!estimate) {
    return null;
  }

  const warningMessage = getCostWarningMessage(estimate);
  const hasError = !estimate.can_afford || estimate.duration_exceeds_limit;

  // Compact mode - just show badge
  if (compact) {
    return (
      <Badge
        variant={hasError ? 'destructive' : 'secondary'}
        className="flex items-center gap-1"
      >
        <Coins className="h-3 w-3" />
        {estimate.credits_required} credits
      </Badge>
    );
  }

  // Full mode - show detailed preview
  return (
    <div className="space-y-2">
      {/* Cost breakdown */}
      <div className="p-4 border rounded-lg bg-muted/30">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium flex items-center gap-2">
            <Coins className="h-4 w-4 text-muted-foreground" />
            Session Cost
          </span>
          <Badge variant={hasError ? 'destructive' : 'default'}>
            {estimate.credits_required} credits
          </Badge>
        </div>

        <div className="text-xs text-muted-foreground space-y-1">
          <div className="flex justify-between">
            <span>Duration:</span>
            <span>{estimate.duration_minutes} minutes</span>
          </div>
          <div className="flex justify-between">
            <span>Rate ({estimate.user_type}):</span>
            <span>{estimate.rate_per_minute} credits/min</span>
          </div>
          <div className="flex justify-between border-t pt-1 mt-1">
            <span>Your balance:</span>
            <span className={estimate.can_afford ? 'text-green-600' : 'text-red-600'}>
              {estimate.current_balance} credits
            </span>
          </div>
          {estimate.can_afford && estimate.balance_after_session !== null && (
            <div className="flex justify-between text-muted-foreground">
              <span>After session:</span>
              <span>{estimate.balance_after_session} credits</span>
            </div>
          )}
        </div>
      </div>

      {/* Warning/Error messages */}
      {warningMessage && (
        <Alert variant={hasError ? 'destructive' : 'warning'}>
          {hasError ? (
            <AlertCircle className="h-4 w-4" />
          ) : (
            <AlertTriangle className="h-4 w-4" />
          )}
          <AlertDescription>{warningMessage}</AlertDescription>
        </Alert>
      )}

      {/* Success indicator */}
      {!hasError && (
        <div className="flex items-center gap-2 text-sm text-green-600">
          <CheckCircle2 className="h-4 w-4" />
          <span>You have enough credits for this session</span>
        </div>
      )}
    </div>
  );
}

export default SessionCostPreview;
